import { useRef, useState } from "react";
import { TextB, TextHOne, ListBullets } from "@phosphor-icons/react";
import BioView from "@/components/BioView";

export const BIO_MAX = 5000;

export default function BioEditor({ value, onChange }) {
  const ref = useRef(null);
  const [tab, setTab] = useState("write");

  const apply = (kind) => {
    const el = ref.current;
    const { selectionStart: s, selectionEnd: e } = el;
    const sel = value.slice(s, e);
    let next, cursor;
    if (kind === "bold") {
      const inner = sel || "bold text";
      next = `${value.slice(0, s)}**${inner}**${value.slice(e)}`;
      cursor = [s + 2, s + 2 + inner.length];
    } else {
      const prefix = kind === "heading" ? "## " : "- ";
      const lineStart = value.lastIndexOf("\n", s - 1) + 1;
      const block = value.slice(lineStart, e) || (kind === "heading" ? "Heading" : "Point");
      const changed = block.split("\n").map(l => l.startsWith(prefix) ? l : prefix + l.replace(/^(#+ |- )/, "")).join("\n");
      next = `${value.slice(0, lineStart)}${changed}${value.slice(Math.max(e, lineStart))}`;
      if (!value.slice(lineStart, e)) next = `${value.slice(0, lineStart)}${changed}${value.slice(lineStart)}`;
      cursor = [lineStart, lineStart + changed.length];
    }
    if (next.length > BIO_MAX) return;
    onChange(next);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(...cursor); });
  };

  const btn = "w-9 h-9 flex items-center justify-center border border-neutral-800 hover:border-red-600 hover:text-red-500 transition-colors";
  const tabBtn = (k) => `lg:hidden font-mono-ui text-[11px] uppercase tracking-[0.25em] pb-1 border-b-2 ${tab === k ? "border-red-600 text-white" : "border-transparent text-neutral-500"}`;

  return (
    <div data-testid="bio-editor">
      <div className="flex items-center gap-2 mt-2">
        <button type="button" className={btn} onClick={() => apply("bold")} title="Bold" data-testid="bio-bold-btn"><TextB size={16} weight="bold" /></button>
        <button type="button" className={btn} onClick={() => apply("heading")} title="Heading" data-testid="bio-heading-btn"><TextHOne size={16} weight="bold" /></button>
        <button type="button" className={btn} onClick={() => apply("bullet")} title="Bullet list" data-testid="bio-bullet-btn"><ListBullets size={16} weight="bold" /></button>
        <div className="ml-auto flex gap-4">
          <button type="button" className={tabBtn("write")} onClick={() => setTab("write")} data-testid="bio-tab-write">Write</button>
          <button type="button" className={tabBtn("preview")} onClick={() => setTab("preview")} data-testid="bio-tab-preview">Preview</button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-3">
        <div className={tab === "write" ? "" : "hidden lg:block"}>
          <textarea ref={ref} data-testid="profile-bio-input" value={value} onChange={(e) => onChange(e.target.value)} rows={14} maxLength={BIO_MAX}
                    placeholder={"## Heading\n\nA paragraph with **bold** words.\n\n- A bullet point"}
                    className="w-full bg-neutral-950 border border-neutral-800 focus:border-red-600 outline-none p-3 font-mono-ui text-sm resize-y transition-colors" />
          <div className={`font-mono-ui text-[11px] mt-2 ${value.length >= BIO_MAX ? "text-red-500" : "text-neutral-500"}`} data-testid="profile-bio-count">
            {value.length.toLocaleString()} / {BIO_MAX.toLocaleString()} characters
          </div>
        </div>
        <div className={tab === "preview" ? "" : "hidden lg:block"}>
          <div className="font-mono-ui text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2 hidden lg:block">Live preview</div>
          <div className="border border-neutral-900 p-4 min-h-[200px] max-h-[420px] overflow-auto">
            {value.trim()
              ? <BioView text={value} testId="bio-preview" className="font-editorial text-lg text-stone-300 leading-snug" />
              : <div className="font-mono-ui text-xs text-neutral-600" data-testid="bio-preview">Your formatted bio will appear here.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
